import { ApiClient } from '../../infra';
import { fromBackend, timeSeriesFromBackend } from '../../domain';
/**
 * O que o painel de um paciente pede ao servidor.
 *
 * As tres perguntas mudam de CAMINHO conforme a origem - paciente em sessao ou
 * link compartilhado -, e `analyticsTarget` e quem sabe disso. Nenhuma outra tela
 * faz essa distincao, e por isso ela mora aqui.
 */
export class PatientAnalyticsApi {
    constructor() {
        this.client = ApiClient.INSTANCE;
    }
    static { this.INSTANCE = new PatientAnalyticsApi(); }
    /**
     * O painel de um paciente so, nas duas origens possiveis.
     *
     * `/v2/dashboard/patient` exige sessao e recebe o id do PACIENTE (`patient.id`,
     * nao `user.id` - sao tabelas diferentes e os numeros nao coincidem).
     * `/v2/dashboard/shared-history` e publico e recebe o UUID do compartilhamento, que e o
     * que permite a mesma tela servir quem nao tem conta.
     *
     * A resposta e o mesmo `DashboardAnalytics` do painel geral, so que sobre uma
     * pessoa - e por isso que o painel por paciente e uma peca reaproveitada e nao
     * uma tela nova.
     */
    async fetchAnalyticsOf(source, query, signal) {
        const [path, body] = this.analyticsTarget(source, query);
        return (await this.client.send('POST', path, body, signal));
    }
    /**
     * Uma serie temporal. Alem das seis metricas, a frequencia cardiaca de ATIVIDADE
     * (tipo 205), que so existe durante um treino e marca os trechos das zonas (B-43).
     */
    async fetchTimeSeriesOf(source, metric, query, signal) {
        const [path, body] = this.analyticsTarget(source, query, `/time-series/${metric}`);
        return timeSeriesFromBackend((await this.client.send('POST', path, body, signal)));
    }
    /**
     * As noites com os ESTAGIOS de sono, para o hipnograma do painel do paciente.
     *
     * Outro endpoint, e nao a serie temporal: `/metrics/SONO` devolve cada noite
     * dividida em intervalos (`details`), e e disso que sai a escada de Acordado,
     * REM, Leve e Pesado. A serie temporal devolveria um ponto por noite com o
     * total, que responde outra pergunta - quanto dormiu, e nao como foi a noite.
     */
    async fetchSleepStagesOf(source, query, signal) {
        const [path, body] = this.analyticsTarget(source, query, '/metrics/SONO');
        const nights = (await this.client.send('POST', path, body, signal));
        // O hipnograma desenha pela hora de cada estagio: as pontas da noite e de cada
        // intervalo vem no fuso de quem olha.
        return nights.map((night) => ({
            ...night,
            date: fromBackend(night.date),
            endDate: fromBackend(night.endDate),
            details: night.details?.map((stage) => ({
                ...stage,
                date: fromBackend(stage.date),
                endDate: fromBackend(stage.endDate)
            }))
        }));
    }
    /**
     * As noites de sono de VARIOS atletas, num intervalo livre.
     *
     * Uma requisicao para o relatorio inteiro. O caminho do hipnograma
     * (`fetchSleepStagesOf`) atende um paciente por vez e so aceita janelas de
     * 1, 7 ou 30 dias - para vinte e um atletas em cinco semanas seriam dezenas de
     * idas para responder uma pergunta so.
     *
     * Aceita profissional, diferente do `/metrics/SONO`. O servidor valida paciente
     * a paciente: pedir quem nao e seu recusa o pedido inteiro.
     */
    /** O caminho e o corpo, que diferem so pela origem. */
    analyticsTarget(source, query, suffix = '') {
        if (source.kind === 'history') {
            return [`/v2/dashboard/shared-history${suffix}`, { ...query, historyId: source.historyId }];
        }
        /**
         * Sem sufixo o caminho e `/v2/dashboard/patient`, e nao `/v2/dashboard/company`.
         *
         * Os dois devolvem o mesmo `DashboardAnalytics`, mas nao aceitam a mesma
         * gente: `company` pede ADMIN ou COMPANY_ADMIN, e `patient` inclui
         * PROFESSIONAL. Cair no primeiro fazia o profissional abrir o painel do
         * PROPRIO paciente e receber 403 - que este servico traduz para "Sessão
         * expirada", entao o aviso ainda mentia o motivo.
         *
         * Com sufixo nao ha escolha a fazer: a serie temporal atende os tres papeis e
         * recebe o paciente no corpo. As metricas detalhadas (`/metrics/SONO`) moram sob
         * `company`, e tambem recebem o paciente no corpo.
         */
        const path = suffix === ''
            ? '/v2/dashboard/patient'
            : suffix.startsWith('/metrics/')
                ? `/v2/dashboard/company${suffix}`
                : `/v2/dashboard${suffix}`;
        return [path, { ...query, patientId: source.patientId, companyId: source.companyId ?? query.companyId }];
    }
}
//# sourceMappingURL=patient-analytics.api.js.map